/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */

export const defaultConfig = {
    threshold: 500, // threshold in ms, while blur is not triggered
    unselectionTimeout: 100 // timeout in ms, while input is unselected
};

let instance = null;
const getInputElements = () => document.querySelectorAll('input[type="text"], textarea, [role="textbox"]');

export default function fullScreenKeyboardInputObserver() {
    if (instance) {
        return instance;
    }

    let isInputSelected = false;
    let browserApi;

    function isFullScreenKeyboardInputAllowed() {
        return document.webkitFullScreenKeyboardInputAllowed !== false;
    }

    function handleInputUnSelection() {
        const isFullscreen = browserApi?.isFullscreen();
        const isInputFocused = isInputFocus();
        if (!isFullscreen && !isInputFocused) {
            browserApi?.enterFullscreen();
            // timeout prevents bouncing between fullscreen and non-fullscreen mode when the next input is focused
            setTimeout(() => {
                isInputSelected = false;
            }, defaultConfig.threshold);
        }
    }

    function handleInputSelection() {
        isInputSelected = true;
    }

    function isInputFocus() {
        const inputElements = getInputElements();
        const inputArray = Array.from(inputElements);
        return inputArray.includes(document.activeElement);
    }

    function isFullScreenAllowed() {
        return isFullScreenKeyboardInputAllowed() || (!isInputFocus() && !isInputSelected);
    }

    const debouncedHandleInputUnSelection = () => setTimeout(handleInputUnSelection, defaultConfig.unselectionTimeout);

    function observeFullScreenKeyboardInput(api) {
        if (isFullScreenKeyboardInputAllowed()) {
            return;
        }
        browserApi = api;
        const inputElements = getInputElements();
        inputElements.forEach(input => {
            input.addEventListener('blur', debouncedHandleInputUnSelection);
            input.addEventListener('focus', handleInputSelection);
        });
    }

    function unsubscribe() {
        if (isFullScreenKeyboardInputAllowed()) {
            return;
        }
        const inputElements = getInputElements();
        inputElements.forEach(input => {
            input.removeEventListener('blur', debouncedHandleInputUnSelection);
            input.removeEventListener('focus', handleInputSelection);
        });
        isInputSelected = false;
    }

    instance = {
        observeFullScreenKeyboardInput,
        isFullScreenAllowed,
        unsubscribe
    };

    return instance;
}
